import { SEARCH_DELAY_MS, queryOfTyped } from '@/lib/session-search';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The text of a search bar and its delay (`recherche-sessions.md` §4.3).
 *
 * The field holds what is TYPED, at once; the address holds what is SEARCHED, and
 * only `delayMs` after the last keystroke (the design system's 200 ms). `commit`
 * is told what the address should say - the search, or `undefined` to drop it -
 * and is NOT called when the text only changed by spaces or does not arm a search
 * (`queryOfTyped` holds): a single character moves nothing.
 *
 * ## The address wins when it moves by itself
 *
 * A back button, a pasted link or « Réinitialiser la recherche » change the
 * address under a field that did not type. The draft then follows it - derived
 * during the render and never synchronised by an effect - unless a keystroke is
 * still waiting for its delay: that one is about to write, and an address that
 * arrived a few milliseconds before it must not erase what is being typed.
 *
 * ## The timer
 *
 * One pending write, replaced by the next keystroke, and cleared when the host
 * unmounts: a write landing after the screen is gone would navigate from the
 * screen the operator already left.
 */

export interface SearchDraft {
  /** What the field shows. */
  readonly typed: string;
  /** Every change of the field - the keystrokes, the cross and Escape included. */
  readonly onTyped: (text: string) => void;
}

export function useSearchDraft(
  urlQuery: string | undefined,
  commit: (query: string | undefined) => void,
  delayMs: number = SEARCH_DELAY_MS,
): SearchDraft {
  const [typed, setTyped] = useState(urlQuery ?? '');
  const [seen, setSeen] = useState(urlQuery);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The latest `commit`, read when the timer fires: the host rebuilds it on every render.
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  });
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  if (urlQuery !== seen) {
    setSeen(urlQuery);
    if (timer.current === null && queryOfTyped(typed) !== urlQuery) setTyped(urlQuery ?? '');
  }

  const onTyped = useCallback(
    (text: string) => {
      setTyped(text);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const wanted = queryOfTyped(text);
        // `null` is HOLD: text that arms nothing leaves the address as it was.
        if (wanted !== null) commitRef.current(wanted);
      }, delayMs);
    },
    [delayMs],
  );

  return { typed, onTyped };
}
